import axios from "axios";

const BASE_URL = process.env.REACT_APP_BACKEND_URL || import.meta?.env?.REACT_APP_BACKEND_URL;
if (!BASE_URL) {
  // eslint-disable-next-line no-console
  console.warn("REACT_APP_BACKEND_URL is not set. Backend calls will fail.");
}

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  timeout: 20000,
});

// Counters API (unique per visitor)
export async function incrementCounter(key, uid) {
  try {
    const resp = await api.post(`/counters/increment`, { key, uid });
    return resp.data;
  } catch (e) {
    return { ok: false };
  }
}

export async function getAdminCounters(passcode) {
  const resp = await api.get(`/admin/counters`, { params: { passcode } });
  return resp.data;
}

export async function setAdminCounter(key, value, passcode) {
  const resp = await api.patch(`/admin/counters`, { key, value }, { params: { passcode } });
  return resp.data;
}

export async function resetAdminCounters(mode, passcode) {
  const resp = await api.post(`/admin/counters/reset`, null, { params: { passcode, mode } });
  return resp.data;
}

// Referrals admin API
export async function resetAdminReferrals(passcode) {
  const resp = await api.post(`/admin/referrals/reset`, null, { params: { passcode } });
  return resp.data;
}

export async function deleteAdminReferralRef(ref, passcode) {
  const resp = await api.delete(`/admin/referrals/${encodeURIComponent(ref)}`, { params: { passcode } });
  return resp.data;
}

export async function getAdminReferralAnalytics(params) {
  const resp = await api.get(`/admin/referrals/analytics`, { params });
  return resp.data;
}

export async function verifyAdminPasscode(passcode) {
  const resp = await api.get(`/admin/auth`, { params: { passcode } });
  return resp.data;
}

export async function sendContactConversationMessage(payload) {
  const resp = await api.post(`/contact/conversations/message`, payload);
  return resp.data;
}

export async function getContactConversations(clientId) {
  const resp = await api.get(`/contact/conversations`, { params: { clientId } });
  return resp.data;
}

export async function getContactConversationMessages(threadId, clientId) {
  const resp = await api.get(`/contact/conversations/${encodeURIComponent(threadId)}/messages`, { params: { clientId } });
  return resp.data;
}

export async function getAdminContactConversations(params) {
  const resp = await api.get(`/admin/contact/conversations`, { params });
  return resp.data;
}

export async function getAdminContactConversation(threadId, passcode) {
  const resp = await api.get(`/admin/contact/conversations/${encodeURIComponent(threadId)}/messages`, { params: { passcode } });
  return resp.data;
}

export async function deleteAdminContactConversation(threadId, passcode) {
  const resp = await api.delete(`/admin/contact/conversations/${encodeURIComponent(threadId)}`, { params: { passcode } });
  return resp.data;
}

export async function adminReplyContactConversation(threadId, message, passcode) {
  const resp = await api.post(`/admin/contact/conversations/${encodeURIComponent(threadId)}/reply`, { message }, { params: { passcode } });
  return resp.data;
}

// Ads API
export async function getActiveAds(uid) {
  const params = uid ? { uid } : undefined;
  const resp = await api.get(`/ads/active`, { params });
  return resp.data;
}

export async function trackAdClick(adId, uid) {
  const resp = await api.post(`/ads/click`, { adId, uid });
  return resp.data;
}

export async function getAdminAds(passcode) {
  const resp = await api.get(`/admin/ads`, { params: { passcode } });
  return resp.data;
}

export async function createAdminAd(formData, passcode) {
  const resp = await api.post(`/admin/ads`, formData, { params: { passcode } });
  return resp.data;
}

export async function updateAdminAd(adId, formData, passcode) {
  const resp = await api.patch(`/admin/ads/${encodeURIComponent(adId)}`, formData, { params: { passcode } });
  return resp.data;
}

export async function deleteAdminAd(adId, passcode) {
  const resp = await api.delete(`/admin/ads/${encodeURIComponent(adId)}`, { params: { passcode } });
  return resp.data;
}

// Guide weekly text overrides
export async function getCourseWeekOverrides() {
  const resp = await api.get(`/course/week-overrides`);
  return resp.data;
}

export async function getAdminCourseWeekOverrides(passcode) {
  const resp = await api.get(`/admin/course/week-overrides`, { params: { passcode } });
  return resp.data;
}

export async function upsertAdminCourseWeekOverride(week, payload, passcode) {
  const resp = await api.put(`/admin/course/week-overrides/${encodeURIComponent(week)}`, payload, { params: { passcode } });
  return resp.data;
}

export async function deleteAdminCourseWeekOverride(week, passcode) {
  const resp = await api.delete(`/admin/course/week-overrides/${encodeURIComponent(week)}`, { params: { passcode } });
  return resp.data;
}

// Forum API
function authHeaders(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function forumGetTopics() {
  const resp = await api.get(`/forum/topics`);
  return resp.data;
}

export async function forumSignup(payload) {
  const resp = await api.post(`/forum/auth/signup`, payload);
  return resp.data;
}

export async function forumLogin(payload) {
  const resp = await api.post(`/forum/auth/login`, payload);
  return resp.data;
}

export async function forumMe(token) {
  const resp = await api.get(`/forum/auth/me`, { headers: authHeaders(token) });
  return resp.data;
}

export async function forumLogout(token) {
  const resp = await api.post(`/forum/auth/logout`, null, { headers: authHeaders(token) });
  return resp.data;
}

export async function forumListPosts({ token, topic, sort, page, pageSize }) {
  const resp = await api.get(`/forum/posts`, {
    params: { topic, sort, page, pageSize },
    headers: authHeaders(token),
  });
  return resp.data;
}

export async function forumCreatePost({ token, topic, title, body }) {
  const resp = await api.post(`/forum/posts`, { topic, title, body }, { headers: authHeaders(token) });
  return resp.data;
}

export async function forumVotePost({ token, postId, value }) {
  const resp = await api.post(`/forum/posts/${encodeURIComponent(postId)}/vote`, { value }, { headers: authHeaders(token) });
  return resp.data;
}

export async function forumListComments({ token, postId, sort, page, pageSize }) {
  const resp = await api.get(`/forum/posts/${encodeURIComponent(postId)}/comments`, {
    params: { sort, page, pageSize },
    headers: authHeaders(token),
  });
  return resp.data;
}

export async function forumCreateComment({ token, postId, body }) {
  const resp = await api.post(`/forum/posts/${encodeURIComponent(postId)}/comments`, { body }, { headers: authHeaders(token) });
  return resp.data;
}

export async function forumCreateReply({ token, postId, body, parentCommentId }) {
  const resp = await api.post(
    `/forum/posts/${encodeURIComponent(postId)}/comments`,
    { body, parentCommentId },
    { headers: authHeaders(token) }
  );
  return resp.data;
}

export async function forumVoteComment({ token, commentId, value }) {
  const resp = await api.post(`/forum/comments/${encodeURIComponent(commentId)}/vote`, { value }, { headers: authHeaders(token) });
  return resp.data;
}

export async function forumDeletePost({ token, postId }) {
  const resp = await api.delete(`/forum/posts/${encodeURIComponent(postId)}`, { headers: authHeaders(token) });
  return resp.data;
}

export async function forumDeleteComment({ token, commentId }) {
  const resp = await api.delete(`/forum/comments/${encodeURIComponent(commentId)}`, { headers: authHeaders(token) });
  return resp.data;
}

export async function adminForumListPosts(params) {
  const resp = await api.get(`/admin/forum/posts`, { params });
  return resp.data;
}

export async function adminForumCreatePost(payload, passcode) {
  const resp = await api.post(`/admin/forum/posts`, payload, { params: { passcode } });
  return resp.data;
}

export async function adminForumDeletePost(postId, passcode) {
  const resp = await api.delete(`/admin/forum/posts/${encodeURIComponent(postId)}`, { params: { passcode } });
  return resp.data;
}

export async function adminForumUsers(params) {
  const resp = await api.get(`/admin/forum/users`, { params });
  return resp.data;
}

export async function adminForumUserActivity(userId, params) {
  const resp = await api.get(`/admin/forum/users/${encodeURIComponent(userId)}/activity`, { params });
  return resp.data;
}